<script lang="ts" setup>
import { useHead } from '@vueuse/head';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import type { ImageBySize } from '@/utils/image';
import type { NutsProduct, NutsProductVariant } from '@/utils/product';

const props = defineProps<{
  image?: ImageBySize;
  product: NutsProduct;
  snackPackMode?: boolean;
  variant: NutsProductVariant;
  webstoreTitle?: string;
}>();

const route = useRoute();

const canonicalUrlPath = computed(() => {
  if (props.product.key === '5026') return '/jordan-almonds/';

  let { path } = route;

  if (props.product.variants[0]?.urlName) {
    const [{ urlName }] = props.product.variants;
    const variantUrlExtension = path.substring(path.indexOf(urlName) + urlName.length);
    if (variantUrlExtension.length && !variantUrlExtension.startsWith('.html')) {
      path = path.replace(variantUrlExtension, '.html');
    }
  }

  return path;
});

const canonicalUrl = computed(() => `https://nuts.com${canonicalUrlPath.value}`);

const ogMetadata = computed(() => {
  const { key, metaDescription: description } = props.product;
  let title = props.product.name;

  const ancestors = props.product.ancestors ?? [];

  if (props.product.ancestors.length) {
    const primaryCategory = ancestors[ancestors.length - 1];
    title = `${title} | ${primaryCategory.name}`;
  }

  title = `${title} | Nuts.com`;
  const metaData = { title, description, image: '', url: '' };
  if (props.image) {
    metaData.image = props.image.large;
  }
  if (!['3011', '9952', '8547', '3131'].includes(key)) {
    metaData.url = canonicalUrl.value;
  }
  return metaData;
});

const headTitle = computed(() => {
  if (props.product && props.product.customPageTitle?.en) return props.product.customPageTitle?.en;
  return props.webstoreTitle ?? ogMetadata.value.title.replace(/( \| )/g, ' — ');
});

const meta = computed(() => {
  const attributes = [
    {
      name: 'description',
      content: ogMetadata.value.description,
    },
    ...Object.entries(ogMetadata.value).map(([key, content]) => ({
      key: `og:${key}`,
      content,
      property: `og:${key}`,
    })),
  ];

  const { variants = [] } = props.product;
  if (variants.length) {
    const hiddenFromSearch = props.product.hiddenFromSearch ?? false;
    const hasSnackPacks = variants.some((v) => v.snackPack) && !variants.every((v) => v.snackPack);
    if (hiddenFromSearch) {
      attributes.push({ name: 'robots', content: 'noindex,nofollow' });
    } else if (props.snackPackMode && hasSnackPacks) {
      attributes.push({ name: 'robots', content: 'noindex' });
    }
  }
  return attributes;
});

useHead(
  computed(() => ({
    title: headTitle.value,
    meta: meta.value,
    link: [
      {
        rel: 'canonical',
        href: canonicalUrl.value,
      },
    ],
    bodyAttrs: {
      class: ['page-pdp'],
    },
  })),
);
</script>
